import { get, flattenDeep } from 'lodash';
import { createSelector } from 'reselect';

import { RecipeCategoryEnum } from 'Common/Constants/Enumeration/RecipeCategoryEnum';
import {
  ReimbursableMealFieldEnum as Fields,
  EditMenuFieldEnum as EditMenuFields,
  RecipeFieldEnum as RecipeFields,
} from 'Common/FieldEnum';
import {
  formatMenuItemForRecipeMap,
  areContributionsModifiedByUser,
  flattenMapToRecipe,
} from 'Pages/managemenu/subpages/editmenu/utilities';
import { getRecipes } from 'Common/Components/RecipeSearch/state/selectors';
import { makeOptionsWithNull } from 'Common/FormHelper';

const root = (state) => get(state, 'ManageMenuState.editMenu');

export const getMenuId = (state) => get(root(state), 'menuId');

export const getNumberOfDays = (state) => get(root(state), 'numberOfDays');

export const getAnalysisDays = (state) => get(root(state), 'analysisDays');

export const getMenuTitle = (state) => get(root(state), 'menuTitle');

export const getFiscalYear = (state) => get(root(state), 'fiscalYear');

export const getNutritionStandardId = (state) =>
  get(root(state), 'nutritionStandardId');

export const getServings = (state) => get(root(state), 'servings');

export const getFetching = (state) => get(root(state), 'fetching');

export const getMenuDaysMap = (state) => get(root(state), 'menuDaysMap');

export const getSaveSuccessful = (state) => get(root(state), 'saveSuccessful');

export const getError = (state) => get(root(state), 'error');

export const getRecipesById = (state) => get(root(state), 'recipesById');

export const getWeeklyRequirements = (state) =>
  get(root(state), 'weeklyRequirements', []);

export const getUnmetDailyRequirements = (state) =>
  get(root(state), 'unmetDailyRequirements', []);

export const getAnalyzing = (state) => get(root(state), 'analyzing', false);

export const getDailyContributions = (state) =>
  get(root(state), 'dailyContributions', []);

export const getRecipeCountByDayMap = (state) =>
  get(root(state), 'recipeCountByDayMap');

export const getCycleMenuViewBy = (state) => get(root(state), 'viewBy');

export const getDisableDeleteRecipe = (state) =>
  get(root(state), 'disableDeleteRecipe');

export const getReimbMealsByDayMap = (state) =>
  get(root(state), 'reimbMealsByDayMap');

export const getIsReadOnly = (state) => get(root(state), 'isReadOnly');

export const getSharedSourceId = (state) => get(root(state), 'sharedSourceId');

export const getIsSharedMenu = (state) => Boolean(getSharedSourceId(state));

export const getReimbMealsForApi = (state) => {
  const reimbMealsByDayMap = getReimbMealsByDayMap(state);
  const ReimbMeals = flattenDeep(
    Object.keys(reimbMealsByDayMap).map((k) =>
      Object.values(reimbMealsByDayMap[k]).map((meal) => {
        const copy = { ...meal };

        Object.keys(Fields).forEach((key) => {
          const parsedValue = parseFloat(meal[key]) || 0;
          copy[key] = parsedValue;
        });

        copy.Children = Object.values(meal.Children);

        return copy;
      })
    )
  );

  ReimbMeals.map((reimbMeal) => {
    reimbMeal[EditMenuFields.UserModified] = false;
    const meals = reimbMealsByDayMap[reimbMeal.DayNumber];
    if (meals) {
      const recipeMap = getRecipeMapOnDay(state)(reimbMeal.DayNumber);
      Object.keys(meals).forEach((mealRecipeId) => {
        const meal = meals[mealRecipeId];
        reimbMeal[EditMenuFields.UserModified] = areContributionsModifiedByUser(
          meal,
          recipeMap
        );
      });
    }
    return reimbMeal;
  });

  return ReimbMeals;
};

/**
 * Returns the list of recipes on a given day.
 */
export const getRecipesOnDay = (state, dayNumber) => {
  const menuDaysMap = getMenuDaysMap(state);
  const recipesById = getRecipesById(state);

  const recipeDraggableIds = menuDaysMap[dayNumber];

  if (!recipeDraggableIds) return [];

  return recipeDraggableIds.map((id) => {
    return recipesById[id];
  });
};

/**
 * Returns list of non entree and non a la carte recipe items on a given day.
 */
export const getNonEntreeAndNonAlaCarteRecipesOnDay = (state, dayNumber) => {
  const recipes = getRecipesOnDay(state, dayNumber);

  return recipes.filter(
    (r) => r.RecipeCategoryId !== RecipeCategoryEnum.Entree && !r.Alacarte
  );
};

export const getReimbMealOnDay = (state, dayNumber, recipeId) => {
  const reimbMealsByDayMap = getReimbMealsByDayMap(state);

  return get(reimbMealsByDayMap, `${dayNumber}.${recipeId}`, null);
};

export const getRecipeMapByDay = createSelector(
  getMenuDaysMap,
  getRecipesById,
  (menuDaysMap, recipesById) => {
    const recipeMapByDay = {};

    Object.keys(menuDaysMap).forEach((dayNumber) => {
      const recipeDraggableIds = menuDaysMap[dayNumber];
      const recipeMap = {};

      recipeDraggableIds.forEach((id) => {
        const recipe = recipesById[id];
        const recipeId = recipe.RecipeId;

        if (!recipe.Alacarte) {
          const updatedRecipe = formatMenuItemForRecipeMap(recipe, recipesById);
          recipeMap[recipeId] = updatedRecipe;
        }
      });

      recipeMapByDay[dayNumber] = recipeMap;
    });

    return recipeMapByDay;
  }
);

/**
 * Returns a map of recipe ids to their recipe. Excludes a la carte items from
 * the map.
 */
export const getRecipeMapOnDay = createSelector(
  getRecipeMapByDay,
  (recipeMapByDay) => (dayNumber) => recipeMapByDay[dayNumber]
);

export const getIsApplying = (state) => get(root(state), 'fetching');

export const getEditableAppliedDays = (state) =>
  get(root(state), 'editableAppliedDays');

export const getRecipeList = createSelector(
  getMenuDaysMap,
  getRecipesById,
  (menuDaysMap, recipesById) => {
    return flattenMapToRecipe(menuDaysMap, recipesById, true);
  }
);

export const getRecipeOptions = createSelector(getRecipes, (recipes) => {
  return makeOptionsWithNull(recipes, {
    keyKey: RecipeFields.Id,
    valueKey: RecipeFields.Id,
    textKeys: [RecipeFields.Id, RecipeFields.Name],
  });
});

export const getSchoolCalendar = (state) => {
  return get(root(state), 'schoolCalendar');
};

export const getSites = (state) => {
  return get(root(state), 'sites');
};



// WEBPACK FOOTER //
// ./src/pages/managemenu/state/editmenu/selectors.js